import { type FC } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  styled,
  accordionClasses,
  accordionSummaryClasses,
  type AccordionProps,
} from '@mui/material'
import { Marker } from '@react-google-maps/api'
import dayjs from 'dayjs'

import { Body1, Button, H6, Grid, Box, Stack, Skeleton } from '@dhl-ui/components'
import { AddressLine, Weekday } from '@dhl/components'
import { Map } from '@dhl/google-maps'

import { ExpandMoreIcon } from '@dhl-ui/icons'
import { type Servicepoint } from './model.js'
import { getFirstOpeningsTime } from './utils.js'
import { ServicepointOpeningTimes } from './ServicepointOpeningTimes.js'
import { type ServicepointListItemProps } from './ServicepointListItem.js'
import { ServicepointIcon } from './ServicepointIcon.js'
import { ServicepointDistanceChip } from './ServicepointDistanceChip.js'

const ServicePointAccordion = styled(Accordion)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  boxShadow: 'unset',

  // FIXME: wait till we get accordion and then propegate divider as it should
  ':last-child': {
    borderBottom: 'none',
  },
  '&::before': {
    backgroundColor: 'unset',
  },
  [`&.${accordionClasses.expanded}`]: {
    boxShadow: 'unset',
    margin: 0,
  },
}))

const ServicePointAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  [`& .${accordionSummaryClasses.content}`]: {
    margin: `${theme.spacing(2)} 0`,
  },
  [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
    alignSelf: 'flex-start',
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}))

const ServicePointAccordionDetails = styled(AccordionDetails)({
  paddingTop: 0,
  marginLeft: '44px',
})

type Props = Omit<ServicepointListItemProps, 'divider' | 'isDesktop' | 'onClick'> &
  Pick<AccordionProps, 'defaultExpanded'>

const ClockLabel: FC<{ firstOpeningTime: Servicepoint['openingsTimes'][0] }> = ({ firstOpeningTime }) => (
  <>
    <Weekday weekDay={firstOpeningTime.weekDay} format="short" /> {' - '}
    {firstOpeningTime.timeFrom.format('H:mm')} - {firstOpeningTime.timeTo.format('H:mm')}
  </>
)

export const ServicepointListItemDesktop: FC<Props> = ({
  defaultExpanded,
  servicepoint,
  onSelect,
  showOpeningTimes = true,
}) => {
  const { t } = useTranslation()
  const firstOpeningTime = getFirstOpeningsTime(servicepoint, dayjs())

  const handleSelect = (item: Servicepoint) => {
    onSelect?.(item)
  }

  if (!firstOpeningTime) {
    return null
  }

  return (
    <ServicePointAccordion
      data-test="servicepoint-list-item"
      data-testid="servicepoint-list-item"
      defaultExpanded={defaultExpanded}
    >
      <ServicePointAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Grid container spacing={1} display="flex" alignItems="center">
          <Grid mx={1}>
            <ServicepointIcon
              servicepointFormat={servicepoint.servicepointFormat}
              servicepointType={servicepoint.servicepointType}
            />
          </Grid>
          <Grid display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
            <ServicepointDistanceChip distance={servicepoint.distance ?? 0} />
            <Stack gap={0.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <H6>{servicepoint.name}</H6>
              </Box>
              <Box>
                <AddressLine singleLine address={servicepoint.address} color="grey.600" />
              </Box>
              {showOpeningTimes && (
                <Box>
                  <Body1>
                    <ClockLabel firstOpeningTime={firstOpeningTime} />
                  </Body1>
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>
      </ServicePointAccordionSummary>
      <ServicePointAccordionDetails>
        <Grid container spacing={2}>
          <Grid size={12}>
            <H6>{t('servicepoint.servicepointDetailsDialog.openingTimesText')}</H6>
          </Grid>
          <Grid size={5}>
            <ServicepointOpeningTimes openingTimes={servicepoint.openingsTimes} />
          </Grid>
          <Grid size={7}>
            <Map
              mapContainerStyle={{ height: '160px' }}
              center={{
                lat: servicepoint.geoLocation.latitude,
                lng: servicepoint.geoLocation.longitude,
              }}
              options={{
                disableDefaultUI: true,
                gestureHandling: 'greedy',
              }}
              zoom={14}
            >
              <Marker
                position={{
                  lat: servicepoint.geoLocation.latitude,
                  lng: servicepoint.geoLocation.longitude,
                }}
              />
            </Map>
          </Grid>
          <Grid>
            <Button
              fullWidth
              onClick={() => handleSelect(servicepoint)}
              color="primary"
              variant="contained"
              data-test="select-button"
            >
              {t(`servicepoint.servicepointDetailsDialog.selectButton.${servicepoint.servicepointType.toLowerCase()}`)}
            </Button>
          </Grid>
        </Grid>
      </ServicePointAccordionDetails>
    </ServicePointAccordion>
  )
}

export const ServicepointListItemDesktopSkeleton: FC = () => (
  <ServicePointAccordion expanded={false}>
    <ServicePointAccordionSummary>
      <Grid container spacing={1} display="flex" alignItems="center">
        <Grid mx={1}>
          <Skeleton variant="rounded" width={24} height={24} />
        </Grid>
        <Grid display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
          <Skeleton variant="rounded" width={64} height={20} />
          <Stack>
            <Stack direction="row" spacing={0.5}>
              <Skeleton variant="text" textVariant="h6" width={80} />
              <Skeleton variant="text" textVariant="h6" width={120} />
            </Stack>
            <Skeleton variant="text" textVariant="body2" width={300} />
          </Stack>
        </Grid>
      </Grid>
    </ServicePointAccordionSummary>
  </ServicePointAccordion>
)
